import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import {
  Send as SendIcon,
  PlayArrow as PlayIcon,
  Edit as EditIcon,
  AttachFile as AttachFileIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Alert,
  Switch,
  FormControlLabel,
} from '@mui/material';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';

import EditTicketDialog from 'components/common/EditTicketDialog';
import config from 'config';
import useCurrentUser from 'hooks/useCurrentUser';
import Pusher from 'pusher-js';
import {
  useGetTicketQuery,
  useAddMessageToTicketMutation,
  useUploadFileToTicketMutation,
  useAnalyzeTicketMutation,
  useGetTicketCostQuery,
  useTransitionTicketMutation,
} from 'services/api/tickets.api';

import EditFileToolComponent from './components/EditFileToolComponent';
import EditPromptToolComponent from './components/EditPromptToolComponent';
import ExecuteQueryToolComponent from './components/ExecuteQueryToolComponent';
import GetClientInfoToolComponent from './components/GetClientInfoToolComponent';
import GrepSearchToolComponent from './components/GrepSearchToolComponent';
import ListDirToolComponent from './components/ListDirToolComponent';
import MarkAnalysisCompleteToolComponent from './components/MarkAnalysisCompleteToolComponent';
import ReadFileToolComponent from './components/ReadFileToolComponent';
import SimpleToolComponent from './components/SimpleToolComponent';
import TicketMessageComponent from './components/TicketMessageComponent';
import TicketStatusChip from './components/TicketStatusChip';
import ToolResponseComponent from './components/ToolResponseComponent';

const Ticket: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const messageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const user = useCurrentUser();
  const { data: ticket, isLoading, refetch } = useGetTicketQuery(Number(ticketId));
  const { data: costData } = useGetTicketCostQuery(Number(ticketId));
  const [addMessage] = useAddMessageToTicketMutation();
  const [uploadFile] = useUploadFileToTicketMutation();
  const [analyzeTicket] = useAnalyzeTicketMutation();
  const [transitionTicket, { isLoading: isTransitioning }] = useTransitionTicketMutation();

  const [newMessage, setNewMessage] = useState('');
  const [showToUser, setShowToUser] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [viewAsRegularUser, setViewAsRegularUser] = useState(true);
  const useRegularUserView = viewAsRegularUser || !user?.isAdmin;

  // Pusher setup for real-time updates
  useEffect(() => {
    if (!ticketId) {
      return undefined;
    }

    const pusher = new Pusher(config.pusherAppKey, {
      cluster: 'sa1',
    });

    const channel = pusher.subscribe(`ticket-${ticketId}`);

    channel.bind('update', () => {
      refetch().catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error refetching ticket:', error);
      });
    });

    return () => {
      pusher.unsubscribe(`ticket-${ticketId}`);
      pusher.disconnect();
    };
  }, [ticketId, refetch]);

  // Auto-scroll to input when history updates
  useEffect(() => {
    if (messageInputRef.current) {
      messageInputRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ticket?.AIHistory]);

  const handleAddMessage = async () => {
    if (!newMessage.trim() || !ticketId) return;

    try {
      await addMessage({
        ticketId: Number(ticketId),
        data: { content: newMessage, showToUser },
      }).unwrap();

      setNewMessage('');
      refetch().catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error refetching ticket:', error);
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error adding message:', error);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !ticketId) return;

    setIsUploadingFile(true);
    setUploadError(null);

    try {
      uploadFile({ ticketId: Number(ticketId), file, showToUser })
        .unwrap()
        .then(() => {
          // Clear file input
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }

          // Refetch ticket to show the new message
          refetch().catch((error) => {
            // eslint-disable-next-line no-console
            console.error('Error refetching ticket:', error);
          });
        })
        .catch((err) => {
          setUploadError('Error al subir el archivo');
          // eslint-disable-next-line no-console
          console.error('Error uploading file:', err);
        });
    } catch (err) {
      setUploadError('Error al subir el archivo');
      // eslint-disable-next-line no-console
      console.error('Error uploading file:', err);
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          // Create a new File object with a name
          const imageFile = new File([file], `pasted-image-${Date.now()}.png`, { type: file.type });

          // Handle the upload with showToUser parameter
          uploadFile({ ticketId: Number(ticketId), file: imageFile, showToUser })
            .unwrap()
            .then(() => {
              // Refetch ticket to show the new message
              refetch().catch((error) => {
                // eslint-disable-next-line no-console
                console.error('Error refetching ticket:', error);
              });
            })
            .catch((error) => {
              // eslint-disable-next-line no-console
              console.error('Error uploading pasted image:', error);
              setUploadError('Error al subir la imagen pegada');
            });
          event.preventDefault();
          break;
        }
      }
    }
  };

  const handlePasteWrapper = (event: React.ClipboardEvent) => {
    handlePaste(event);
  };

  const handleAnalyzeTicket = async () => {
    if (!ticketId) return;

    try {
      await analyzeTicket(Number(ticketId)).unwrap();
      refetch().catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error refetching ticket:', error);
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error analyzing ticket:', error);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleAddMessage().catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error handling key press:', error);
      });
    }
  };

  const handleTicketTransition = (event: 'CLOSE' | 'NEW_MESSAGE') => {
    if (!ticketId) return;
    transitionTicket({ ticketId: Number(ticketId), data: { event } })
      .unwrap()
      .then(refetch)
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error marking as completed:', error);
      });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px">
        <Typography variant="h6" color="text.secondary" mb={2}>
          Ticket no encontrado
        </Typography>
        <Button onClick={() => navigate('/tickets')}>Volver a Tickets</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '100%', margin: 'auto', p: 2 }}>
      {/* Header */}
      <Box mb={3}>
        {/* Top row: Back button, title, and action buttons */}
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <IconButton
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/tickets');
              }
            }}
            size="medium"
            sx={{ mr: 1 }}
          >
            <ArrowBackIcon fontSize="medium" />
          </IconButton>
          <Typography variant="h5" component="h1" sx={{ flex: 1, fontWeight: 500 }}>
            {ticket.title}
          </Typography>
          <Box display="flex" gap={1} alignItems="center">
            {user?.isAdmin && (
              <FormControlLabel
                control={
                  <Switch
                    checked={viewAsRegularUser}
                    onChange={() => setViewAsRegularUser((prev) => !prev)}
                    color="primary"
                  />
                }
                label="Ver como usuario normal"
                sx={{ mr: 1 }}
              />
            )}
            <Button variant="contained" startIcon={<EditIcon />} onClick={() => setEditDialogOpen(true)} size="small">
              Editar
            </Button>
            {user?.isAdmin && (
              <Button
                variant="contained"
                startIcon={<PlayIcon />}
                onClick={() => {
                  handleAnalyzeTicket().catch(console.error); // eslint-disable-line no-console
                }}
                disabled={ticket?.status === 'ANALYSIS_WITH_AI'}
                size="medium"
                sx={{ ml: 1 }}
              >
                Analizar
              </Button>
            )}
            {/* Status transition buttons */}
            {ticket.status === 'CLOSED' ? (
              <Button
                variant="contained"
                color="warning"
                onClick={() => handleTicketTransition('NEW_MESSAGE')}
                disabled={isTransitioning}
                size="small"
              >
                Marcar como pendiente
              </Button>
            ) : (
              <Button
                variant="contained"
                color="success"
                onClick={() => handleTicketTransition('CLOSE')}
                disabled={isTransitioning}
                size="small"
              >
                Marcar como completado
              </Button>
            )}
          </Box>
        </Box>

        {/* Description */}
        <Typography variant="body1" color="text.secondary" mb={2} sx={{ whiteSpace: 'pre-line' }}>
          {showFullDescription ? ticket.description : ticket.description.substring(0, 1000)}
          {ticket.description.length > 1000 && (
            <Button
              size="small"
              onClick={() => setShowFullDescription(!showFullDescription)}
              sx={{ mt: 1, p: 0, minWidth: 'auto' }}
            >
              {showFullDescription ? 'Ver menos' : 'Ver más'}
            </Button>
          )}
        </Typography>

        {/* Ticket details */}
        <Stack direction="row" spacing={2} alignItems="center" divider={<Divider orientation="vertical" flexItem />}>
          <TicketStatusChip status={ticket.status} size="small" />
          {costData && costData.cost > 0 && (
            <Typography variant="caption" color="text.secondary">
              Costo: ${costData?.cost.toFixed(2)}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            Creado: {new Date(ticket.createdAt).toLocaleString('es-CL')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Última actualización: {new Date(ticket.updatedAt).toLocaleString('es-CL')}
          </Typography>
        </Stack>
      </Box>

      {/* AIHistory Display */}
      <Box mb={3}>
        {ticket.AIHistory?.length === 0 && (
          <Box sx={{ textAlign: 'center', my: 6 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Esperando a que el equipo de soporte responda...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Puedes agregar un mensaje o más información si quieres.
            </Typography>
          </Box>
        )}
        {(ticket.AIHistory || [])
          .filter((message) => {
            if (!useRegularUserView) return true;
            return Boolean(message.name);
          })
          .map((message, index) => (
            <Box
              key={`history-${ticket.id}-${index + 1}-${message.role}`}
              display="flex"
              justifyContent="flex-start"
              m={2}
              mt={1}
              flexDirection="column"
            >
              {/* Render user and assistant messages */}
              {message.role === 'user' && <TicketMessageComponent message={message} />}
              {message.role === 'assistant' && message.content && <TicketMessageComponent message={message} />}

              {/* Render tool calls */}
              {message.toolCalls?.map((toolCall) => (
                <Box key={toolCall.id} display="flex" justifyContent="flex-start" mt={1} flexDirection="column">
                  {toolCall.function.name === 'listDir' && <ListDirToolComponent args={toolCall.function.arguments} />}
                  {toolCall.function.name === 'readFile' && (
                    <ReadFileToolComponent args={toolCall.function.arguments} />
                  )}
                  {toolCall.function.name === 'editFile' && (
                    <EditFileToolComponent args={toolCall.function.arguments} />
                  )}
                  {toolCall.function.name === 'editBusinessUnitPrompt' && (
                    <EditPromptToolComponent args={toolCall.function.arguments} />
                  )}
                  {toolCall.function.name === 'grepSearch' && (
                    <GrepSearchToolComponent args={toolCall.function.arguments} />
                  )}
                  {toolCall.function.name === 'executeQuery' && (
                    <ExecuteQueryToolComponent args={toolCall.function.arguments} />
                  )}
                  {toolCall.function.name === 'getClientInfo' && (
                    <GetClientInfoToolComponent args={toolCall.function.arguments} />
                  )}
                  {toolCall.function.name === 'markAnalysisComplete' && (
                    <MarkAnalysisCompleteToolComponent args={toolCall.function.arguments} />
                  )}
                  {/* Simple tools with generic component */}
                  {[
                    'fileSearch',
                    'getDatabaseSchema',
                    'getBusinessUnitDetails',
                    'requestRecommendation',
                    'readLogs',
                  ].includes(toolCall.function.name) && (
                    <SimpleToolComponent toolName={toolCall.function.name} args={toolCall.function.arguments} />
                  )}
                </Box>
              ))}

              {/* Render tool responses */}
              {message.role === 'tool' && <ToolResponseComponent message={message} history={ticket.AIHistory || []} />}
            </Box>
          ))}
      </Box>

      {/* Upload Error Alert */}
      {uploadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
          {uploadError}
        </Alert>
      )}

      {/* Message Input */}
      <Box display="flex" gap={2} alignItems="center" ref={messageInputRef}>
        <Box display="flex" flexDirection="column" gap={1} flex={1}>
          <TextField
            multiline
            minRows={1}
            maxRows={4}
            fullWidth
            placeholder="Agrega un mensaje aquí"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            onPaste={handlePasteWrapper}
            variant="outlined"
          />
        </Box>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/*,.pdf,.doc,.docx,.txt,.csv,.json,.xls,.xlsx,video/*"
          onChange={(e) => {
            handleFileUpload(e).catch((error) => {
              // eslint-disable-next-line no-console
              console.error('Error in onChange:', error);
            });
          }}
          disabled={isUploadingFile}
        />
        {user?.isAdmin && (
          <FormControlLabel
            control={<Switch checked={showToUser} onChange={(e) => setShowToUser(e.target.checked)} size="small" />}
            label="Mostrar al usuario final"
            sx={{ ml: 1 }}
          />
        )}
        <IconButton
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploadingFile}
          sx={{
            height: '56px',
            width: '56px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AttachFileIcon />
        </IconButton>
        <Button
          variant="contained"
          onClick={() => {
            handleAddMessage().catch((error) => {
              // eslint-disable-next-line no-console
              console.error('Error in onClick:', error);
            });
          }}
          disabled={isUploadingFile}
          sx={{
            height: '56px',
            minWidth: '56px',
            px: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isUploadingFile ? <CircularProgress size={24} /> : <SendIcon />}
        </Button>
      </Box>

      {/* Edit Dialog */}
      {ticket && (
        <EditTicketDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          ticket={ticket}
          onSuccess={() => {
            refetch().catch((error) => {
              // eslint-disable-next-line no-console
              console.error('Error refetching after edit:', error);
            });
          }}
        />
      )}
    </Box>
  );
};

export default Ticket;
